import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Stack, Checkbox, TableCell, TableRow, Chip, IconButton, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { GridLayout } from 'shared/ui/grid-layout';
import { EditTaskDialog } from 'features/tasks/edit-task-dialog';
import { tasksApi } from 'features/tasks/api';
import { IPagingData, emptyPagedCollection, TaskDto } from '@sr/dto';
import ManagerSpan from 'shared/ui/manager-span';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { getTaskStatusMeta } from 'features/tasks/task-status-meta';

export const TasksListTab = ({
  checkedItems,
  setCheckedItems,
  onPageIdsChange,
  onCountChange,
}: {
  checkedItems: number[];
  setCheckedItems: (updater: (prev: number[]) => number[]) => void;
  onPageIdsChange: (ids: number[]) => void;
  onCountChange: (count: number) => void;
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [editedTask, setEditedTask] = useState<TaskDto | null>(null);
  const [searchParams] = useSearchParams();
  const [paging, setPaging] = useState<IPagingData>({ page: 0, itemsPerPage: 25 });

  const filter = searchParams.toString();
  const { data: pageData = emptyPagedCollection, isLoading, error } = tasksApi.useGetTasksPagedQuery({ paging, filter }, { refetchOnMountOrArgChange: 60 });

  useEffect(() => {
    onPageIdsChange(pageData.items.map((i) => i.id));
    onCountChange(pageData.items.length);
  }, [pageData, onPageIdsChange, onCountChange]);

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <GridLayout
        columns={[
          '',
          {
            label: (
              <Checkbox
                size="small"
                checked={pageData.items.length > 0 && pageData.items.every((i) => checkedItems.includes(i.id))}
                indeterminate={pageData.items.some((i) => checkedItems.includes(i.id)) && !pageData.items.every((i) => checkedItems.includes(i.id))}
                onChange={(e, checked) => {
                  const ids = pageData.items.map((i) => i.id);
                  setCheckedItems((prev) => (checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))));
                }}
              />
            ),
            align: 'left',
            padding: 'checkbox',
          },
          '№',
          'Тема',
          'Менеджер',
          'Сотрудник',
          'Срок',
          'Статус',
          '',
        ]}
        items={pageData.items}
        isLoading={isLoading}
        error={extractNestJsErrorMessage(error)}
        noItemsText="Список задач пуст"
        pagingData={paging}
        totalItemsCount={pageData.totalItemsCount}
        onChangePaging={(p) => setPaging(p)}
        itemRender={(t: TaskDto) => {
          const statusMeta = getTaskStatusMeta(t.status);
          return (
            <TableRow key={`${t.id}-${checkedItems.includes(t.id) ? '1' : '0'}`} hover selected={checkedItems.includes(t.id)}>
              <TableCell sx={{ p: 0, width: 10, position: 'relative', overflow: 'hidden' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: 10,
                    bgcolor: statusMeta.stripeColor,
                    pointerEvents: 'none',
                  }}
                />
              </TableCell>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  color="primary"
                  checked={checkedItems.includes(t.id)}
                  onChange={(e, checked) => setCheckedItems((prev) => (checked ? [...new Set([...prev, t.id])] : prev.filter((x) => x !== t.id)))}
                />
              </TableCell>
              <TableCell>
                <Link to={`/events/tasks/${t.id}`} style={{ textDecoration: 'none' }}>
                  {t.id}
                </Link>
              </TableCell>
              <TableCell>
                <div>{t.theme}</div>
                {t.description && <div style={{ color: '#666', fontSize: 12 }}>{t.description}</div>}
              </TableCell>
              <TableCell>
                {t.manager ? (
                  <Link to={`/users/${t.manager.id}`} style={{ textDecoration: 'none' }}>
                    <ManagerSpan person={t.manager} />
                  </Link>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>
                {t.employees && t.employees.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {t.employees.map((emp) => (
                      <Link key={emp.id} to={`/users/${emp.id}`} style={{ textDecoration: 'none' }}>
                        <ManagerSpan person={emp} />
                      </Link>
                    ))}
                  </Stack>
                ) : (
                  '-'
                )}
              </TableCell>
              <TableCell>{t.dueDate ? format(new Date(t.dueDate), 'dd.MM.yyyy', { locale: ru }) : '-'}</TableCell>
              <TableCell>
                <Chip size="small" label={statusMeta.label} color={statusMeta.chipColor} variant="outlined" />
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditedTask(t);
                    setEditOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          );
        }}
      />
      {/* Edit dialog */}
      {editedTask && (
        <EditTaskDialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
          }}
          task={editedTask}
        />
      )}
    </Stack>
  );
};

export default TasksListTab;
